/**
 * Shared tracking utilities — used by both the tracking widget (client)
 * and the my-shipments pages (server).
 */

// ISO 6346 size/type code decoder
// Char 1: length (2=20', 4=40', L=45', M=48')
// Char 2: height (0/2/3=standard 8'6", 5/6=9'6" high cube)
// Char 3: type (G=GP/DV, R=Reefer, U=Open Top, P=Flat Rack, T=Tank)

const ISO_LENGTH: Record<string, string> = {
  '1': "10'", '2': "20'", '3': "30'", '4': "40'",
  'L': "45'", 'M': "48'", 'N': "49'",
};

const ISO_HEIGHT_HC = new Set(['5', '6', '9', 'D', 'E']);

const ISO_TYPE: Record<string, string> = {
  'G': 'Dry Van', 'GP': 'Dry Van',
  'R': 'Reefer', 'RE': 'Reefer', 'RS': 'Reefer',
  'U': 'Open Top', 'UT': 'Open Top',
  'P': 'Flat Rack', 'PL': 'Flat Rack', 'PC': 'Flat Rack Collapsible',
  'T': 'Tank', 'TN': 'Tank', 'TG': 'Tank',
  'B': 'Bulk', 'BU': 'Bulk', 'BK': 'Bulk',
  'V': 'Ventilated', 'VH': 'Ventilated',
  'H': 'Insulated',
  'S': 'Livestock',
};

export function decodeISOEquipment(code: string | undefined): string | null {
  if (!code || code.length < 2) return null;

  const lengthChar = code.charAt(0);
  const heightChar = code.charAt(1);
  const length = ISO_LENGTH[lengthChar];
  if (!length) return code;

  const isHighCube = ISO_HEIGHT_HC.has(heightChar);

  if (/^\d+$/.test(code)) {
    return `${length}${isHighCube ? ' High Cube' : ''}`;
  }

  const typeStr = code.slice(2);
  const typeChar = typeStr.charAt(0);
  const type = ISO_TYPE[typeStr.slice(0, 2)] ?? ISO_TYPE[typeChar] ?? '';

  const parts = [length];
  if (isHighCube) parts.push('High Cube');
  if (type) parts.push(type);
  return parts.join(' ');
}

export const EVENT_LABELS: Record<string, string> = {
  DEPA: 'Departed',
  ARRI: 'Arrived',
  LOAD: 'Loaded on vessel',
  DISC: 'Discharged from vessel',
  GTIN: 'Gate in',
  GTOT: 'Gate out',
  STUF: 'Container stuffed',
  STRP: 'Container stripped',
  RECE: 'Received',
  DRFT: 'Draft',
  PENA: 'Pending approval',
  CONF: 'Confirmed',
  ISSU: 'Issued',
  APPR: 'Approved',
  SURR: 'Surrendered',
  CMPL: 'Completed',
  VOID: 'Voided',
};

export interface SnapshotEvent {
  eventType?: string;
  eventClassifierCode?: string;
  eventDateTime?: string;
  transportEventTypeCode?: string;
  equipmentEventTypeCode?: string;
  shipmentEventTypeCode?: string;
  UNLocationCode?: string;
  locationName?: string;
  vesselName?: string;
  voyageNumber?: string;
  equipmentReference?: string;
  ISOEquipmentCode?: string;
  emptyIndicatorCode?: string;
  description?: string;
  [key: string]: unknown;
}

export function eventLabel(e: SnapshotEvent): string {
  const code = e.transportEventTypeCode ?? e.equipmentEventTypeCode ?? e.shipmentEventTypeCode ?? '';
  return EVENT_LABELS[code] ?? code;
}

export interface ContainerInfo {
  reference: string;
  isoCode?: string;
  decoded?: string;
}

export function extractContainersFromEvents(events: SnapshotEvent[]): ContainerInfo[] {
  const map = new Map<string, ContainerInfo>();
  for (const e of events) {
    if (e.equipmentReference && !map.has(e.equipmentReference)) {
      map.set(e.equipmentReference, {
        reference: e.equipmentReference,
        isoCode: e.ISOEquipmentCode,
        decoded: decodeISOEquipment(e.ISOEquipmentCode) ?? undefined,
      });
    }
  }
  return [...map.values()];
}

export function groupContainersByType(containers: ContainerInfo[]): { label: string; count: number }[] {
  const groups = new Map<string, number>();
  for (const c of containers) {
    const key = c.decoded ?? c.isoCode ?? 'Unknown';
    groups.set(key, (groups.get(key) ?? 0) + 1);
  }
  return [...groups.entries()].map(([label, count]) => ({ label, count }));
}

export function getLatestActualEvent(events: SnapshotEvent[]): SnapshotEvent | null {
  const actual = events.filter((e) => e.eventClassifierCode === 'ACT');
  if (actual.length === 0) return null;
  return actual.reduce((latest, e) =>
    (e.eventDateTime ?? '') > (latest.eventDateTime ?? '') ? e : latest
  );
}

export function findArrivalAtDestination(
  events: SnapshotEvent[],
  destinationUnLocode: string | null | undefined,
): SnapshotEvent | null {
  let destLoc = destinationUnLocode ?? null;
  if (!destLoc) {
    const seen = new Set<string>();
    let lastUnLoc: string | null = null;
    for (const e of events) {
      if (e.eventType !== 'TRANSPORT') continue;
      const code = e.UNLocationCode;
      if (!code || seen.has(code)) continue;
      seen.add(code);
      lastUnLoc = code;
    }
    destLoc = lastUnLoc;
  }
  if (!destLoc) return null;

  const matches = events.filter(
    (e) =>
      e.eventClassifierCode === 'ACT' &&
      e.UNLocationCode === destLoc &&
      (e.transportEventTypeCode === 'ARRI' ||
        e.equipmentEventTypeCode === 'DISC' ||
        e.equipmentEventTypeCode === 'GTIN'),
  );
  if (matches.length === 0) return null;
  return matches.reduce((earliest, e) =>
    (e.eventDateTime ?? '') < (earliest.eventDateTime ?? '') ? e : earliest,
  );
}

// First and last unique TRANSPORT stop, in event order. Mirrors
// extractRoute() in tracking-widget.tsx. Used to derive origin /
// destination names from the events snapshot when the persisted
// columns on tracked_shipments are null (e.g. saved before vessel events
// were available).
export function extractRouteEndpoints(events: SnapshotEvent[]): { origin: string | null; destination: string | null } {
  const seen = new Set<string>();
  const stops: string[] = [];
  for (const e of events) {
    if (e.eventType !== 'TRANSPORT') continue;
    const key = e.UNLocationCode ?? e.locationName ?? '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    stops.push(e.locationName ?? e.UNLocationCode ?? key);
  }
  if (stops.length === 0) return { origin: null, destination: null };
  return { origin: stops[0], destination: stops[stops.length - 1] };
}

export function extractRouteUnlocodes(events: SnapshotEvent[]): { origin: string | null; destination: string | null } {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const e of events) {
    if (e.eventType !== 'TRANSPORT') continue;
    const code = e.UNLocationCode;
    if (!code || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  if (codes.length === 0) return { origin: null, destination: null };
  return { origin: codes[0], destination: codes[codes.length - 1] };
}
